/* eslint complexity: ["error", { max: 9 }] -- applies to entire file */
import { useEffect, useState } from "react";
import type { PropsWithChildren } from "react";

import FormField from "@/ui/composed/form/FormField";
import type {
  FormFieldProps,
  ValidationSchema,
} from "@/ui/composed/form/types";
import { Button, FAIconName, Text } from "@/ui/core";
import { BaseCard, Box, HStack, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { validateFields } from "@/utils/form.util";
import { joinTestIDs } from "@/utils/string.util";

interface FormFieldGroupProps extends ViewWithTestID, PropsWithChildren {
  fields: Omit<
    FormFieldProps,
    "isHidden" | "isRequired" | "onChange" | "testID" | "type" | "value"
  >[];
  heading?: string;
  initialValues?: Record<string, FormFieldProps["value"]>;
  onChange?: (fieldValues: Record<string, FormFieldProps["value"]>) => void;
  onSave?: (fieldValues: Record<string, FormFieldProps["value"]>) => void;
  validationSchema: ValidationSchema<string>;
}

/**
 * FormFieldGroup - Uncontrolled form component that manages a group of fields
 *
 * @param initialValues - Initial field values (used only on mount)
 * @param onChange - Called when any field changes. Receives current state of all touched fields.
 *                   Parent should merge with existing state: `(prev) => ({ ...prev, ...values })`
 * @param onSave - Called when Save button is pressed with valid data. Receives validated field values.
 *
 * @example
 * const [formData, setFormData] = useState({ firstName: "", lastName: "" });
 *
 * <FormFieldGroup
 *   fields={[...]}
 *   initialValues={formData}
 *   onChange={(values) => setFormData((prev) => ({ ...prev, ...values }))}
 *   onSave={(values) => submitForm(values)}
 * />
 */
const FormFieldGroup = ({
  children,
  fields,
  heading,
  initialValues,
  onChange,
  onSave,
  testID,
  validationSchema,
}: FormFieldGroupProps) => {
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [values, setValues] = useState<Record<string, FormFieldProps["value"]>>(
    initialValues ?? {},
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    onChange?.(values);
  }, [values, onChange]);

  const validateForm = (): boolean => {
    const validationResult = validateFields({
      validationSchema,
      values,
    });

    setFieldErrors(validationResult.errors);
    return validationResult.isValid;
  };

  const handleSave = () => {
    const isValid = validateForm();

    if (isValid) {
      onSave?.(values);
      setIsReadOnly(true);
      setFieldErrors({});
    }
  };

  const handleFieldChange =
    (fieldName: string) => (value: FormFieldProps["value"]) => {
      setValues((prev) => ({
        ...prev,
        [fieldName]: value,
      }));

      setFieldErrors((prev) => {
        if (!prev[fieldName]) return prev;
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
    };

  const isEditButtonVisible = isReadOnly && onSave;

  return (
    <BaseCard testID={testID} variant="secondary">
      <VStack gap={12}>
        {(heading || isEditButtonVisible) && (
          <HStack
            alignItems="center"
            gap={12}
            height={32}
            justifyContent="space-between"
          >
            {heading && (
              <Text testID={joinTestIDs(testID, "heading")} variant="h5">
                {heading}
              </Text>
            )}
            {isEditButtonVisible && (
              <Button
                leftIcon={FAIconName.Pen}
                onPress={() => {
                  setIsReadOnly(false);
                  setFieldErrors({});
                }}
                size="small"
                testID={joinTestIDs(testID, "edit")}
                title="Edit"
                variant="tertiary"
              />
            )}
          </HStack>
        )}
        {/* eslint-disable-next-line max-params */}
        {fields.map((field, index) => {
          const isBorderless =
            !isReadOnly || validationSchema[field.name].isHidden || index === 0;
          return (
            <Box
              borderColor={ColorToken.NeutralLine}
              borderTopWidth={isBorderless ? 0 : 1}
              key={field.name}
              paddingTop={isBorderless ? undefined : 12}
            >
              <FormField
                // eslint-disable-next-line react/jsx-props-no-spreading, @typescript-eslint/consistent-type-assertions
                {...({
                  ...field,
                  ...validationSchema[field.name],
                  hasError: fieldErrors[field.name] || field.hasError,
                  isReadOnly,
                  onChange: handleFieldChange(field.name),
                  testID: joinTestIDs(testID, field.name),
                  value: values[field.name],
                } as FormFieldProps)}
              />
            </Box>
          );
        })}
        {children}
        {!isReadOnly && onSave && (
          <Button
            isFullWidth
            leftIcon={FAIconName.Check}
            onPress={handleSave}
            testID={joinTestIDs(testID, "save")}
            title="Save"
          />
        )}
      </VStack>
    </BaseCard>
  );
};

export type { FormFieldGroupProps };
export default FormFieldGroup;
